import { useField } from "@rvf/react-router";
import { matchSorter } from "match-sorter";
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouteLoaderData } from "react-router";
import Select, {
  type InputProps,
  type ControlProps,
  type InputActionMeta,
  type Options,
} from "react-select";
import Creatable from "react-select/creatable";
import classNames from "classnames";
import { useJsAvailable } from "~/components/hooks/useJsAvailable";
import { type RootLoader } from "~/root";
import type { DataListOptions } from "~/services/dataListOptions/getDataListOptions";
import InputError from "../error/InputError";
import AutoSuggestClearInput from "./AutoSuggestClearInput";
import AutoSuggestController from "./AutoSuggestController";
import AutoSuggestCustomInput from "./AutoSuggestCustomInput";
import customStyles from "./customStyles";
import Fuse from "fuse.js/basic";

import AutoSuggestFormatOptionLabel from "./AutoSuggestFormatOptionLabel";
import AutoSuggestValueContainer from "./AutoSuggestValueContainer";
import { type AutoSuggestInputProps } from "./types";
import useDataListOptions from "./hooks/useDataListOptions";
import useLiveMessage from "./hooks/useLiveMessage";
import TextInput from "../text/TextInput";
import {
  ariaLiveMessages,
  screenReaderStatus,
} from "./accessibilityConfig/ariaLiveMessages";
import { InputLabel } from "../label/InputLabel";
import { InputHelperText } from "../helperText/InputHelperText";
import { widthClassname } from "../inputWidth";

const MINIMUM_SEARCH_SUGGESTION_CHARACTERS = 3;
const AIRPORT_CODE_LENGTH = 3;
const MILLISECONDS_TIME_OUT_FOCUS_INPUT = 10;

const focusOnInput = (inputId: string) => {
  setTimeout(function () {
    const inputElement = document.querySelector<HTMLInputElement>(
      `#${inputId}`,
    );

    if (inputElement) {
      inputElement.focus();
    }
  }, MILLISECONDS_TIME_OUT_FOCUS_INPUT);
};

// When leave the input, it should focus on the exclusion button when it uses the tab, due problems for change the className in the component CustomValueContainer
const keyDownOnInput = (
  inputId: string,
  buttonExclusionRef: RefObject<HTMLButtonElement | null>,
) => {
  const inputElement = document.querySelector<HTMLInputElement>(`#${inputId}`);

  if (inputElement) {
    inputElement.addEventListener("keydown", function (event: KeyboardEvent) {
      // Only tab without shiftKey
      if (event.key === "Tab" && !event.shiftKey) {
        setTimeout(function () {
          if (buttonExclusionRef.current !== null) {
            buttonExclusionRef.current.focus();
          }
        }, 100);
      }
    });
  }
};

const getSortingAirportsByCode = (
  filteredOptions: DataListOptions[],
  inputCode: string,
): DataListOptions[] => {
  if (inputCode.length === AIRPORT_CODE_LENGTH) {
    return matchSorter(filteredOptions, inputCode, {
      keys: ["value", "subDescription"],
      threshold: matchSorter.rankings.NO_MATCH,
    });
  }

  return filteredOptions;
};

const AutoSuggestInput = ({
  name,
  label,
  placeholder,
  errorMessages,
  helperText,
  dataList,
  dataListArgument,
  noSuggestionMessage,
  isDisabled,
  suffix,
  minSuggestCharacters = MINIMUM_SEARCH_SUGGESTION_CHARACTERS,
  supportsFreeText: isCreatable = false,
  size = "large",
}: AutoSuggestInputProps) => {
  const items = useDataListOptions(dataList, dataListArgument);
  const [currentItemValue, setCurrentItemValue] =
    useState<DataListOptions | null>();
  const field = useField(name);
  const { defaultValue } = field.getInputProps();
  const errorId = `${name}-error`;
  const hasError = (field.error()?.length ?? 0) > 0;
  const inputId = `input-${name}`;
  const helperId = `${name}-helper`;
  const buttonExclusionRef = useRef<HTMLButtonElement>(null);
  const fuzzySearchEngine = useMemo(
    () =>
      new Fuse(items, {
        keys: ["label"],
        threshold: 0.4,
        minMatchCharLength: 5,
      }),
    [items],
  );

  const jsAvailable = useJsAvailable();
  const [optionWasSelected, setOptionWasSelected] = useState(false);
  const { liveMessage, liveMessageKey, announceLiveMessage } = useLiveMessage();
  const [options, setOptions] = useState<DataListOptions[]>([]);
  const rootLoaderData = useRouteLoaderData<RootLoader>("root");

  const onInputChange = (value: string, { action }: InputActionMeta) => {
    if (action === "input-change") {
      if (value.length < minSuggestCharacters) {
        setOptions([]);
        return;
      }

      let filteredOptions = items.filter((item) =>
        item.label.toLowerCase().includes(value.toLocaleLowerCase()),
      );

      // In case is the airports list, sorting by the code
      if (dataList === "airports") {
        filteredOptions = getSortingAirportsByCode(filteredOptions, value);
      }

      // In case no match is found and the list is street names, it will try to find a match with the fuzzy search
      if (filteredOptions.length === 0 && dataList === "streetNames") {
        filteredOptions = fuzzySearchEngine
          .search(value)
          .map((result) => result.item);
      }

      setOptions(filteredOptions);
      if (filteredOptions.length === 0) {
        announceLiveMessage(noSuggestionMessage ?? "");
      }
    }
  };

  useEffect(() => {
    keyDownOnInput(inputId, buttonExclusionRef);
  });

  useEffect(() => {
    let value =
      items.find((dataOption) => dataOption.value === defaultValue) ?? null;
    if (isCreatable && !value && defaultValue) {
      value = { value: defaultValue, label: defaultValue };
    }

    // oxlint-disable-next-line react/set-state-in-effect
    setCurrentItemValue(value);
  }, [defaultValue, items, isCreatable]);

  const MemoizedController = useCallback(
    (props: ControlProps<DataListOptions, false>) =>
      AutoSuggestController(props, isDisabled),
    [isDisabled],
  );

  const MemoizedCustomInput = useCallback(
    (props: InputProps<DataListOptions, false>) =>
      AutoSuggestCustomInput(props, hasError),
    [hasError],
  );

  // In case user does not have Javascript, it should render the Input as suggestion input
  if (!jsAvailable) {
    return (
      <TextInput
        name={name}
        label={label}
        placeholder={placeholder}
        errorMessages={errorMessages}
      />
    );
  }

  const SelectComponent = isCreatable ? Creatable : Select;

  return (
    <div
      data-testid={items.length > 0 ? `${inputId}-loaded` : ""}
      className={classNames("w-full kern-form-input", {
        "kern-form-input--error": hasError,
      })}
    >
      <div className="flex flex-col gap-kern-space-small pb-kern-space-small">
        {label && <InputLabel name={inputId} label={label} suffix={suffix} />}

        {helperText && (
          <InputHelperText helperText={helperText} helperId={helperId} />
        )}
      </div>

      <div className={classNames(widthClassname(size))}>
        <SelectComponent
          aria-describedby={[
            field.error() && errorId,
            helperText && helperId,
          ].join("")}
          aria-invalid={field.error() !== null}
          {...(isCreatable && {
            formatCreateLabel: (creatableValue) => creatableValue,
            isValidNewOption: (
              input,
              _value: Options<DataListOptions>,
              selectOptions,
            ) =>
              selectOptions.length < 2 && input.length >= minSuggestCharacters,
          })}
          ariaLiveMessages={ariaLiveMessages(
            rootLoaderData?.accessibilityTranslations,
          )}
          className={classNames("w-full ph-no-capture", {
            "option-was-selected": optionWasSelected,
          })}
          components={{
            ClearIndicator: (props) =>
              AutoSuggestClearInput(props, buttonExclusionRef),
            Control: MemoizedController,
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
            Input: MemoizedCustomInput,
            ValueContainer: AutoSuggestValueContainer,
          }}
          filterOption={() => true}
          formatOptionLabel={AutoSuggestFormatOptionLabel}
          id={name}
          inputId={inputId}
          instanceId={name}
          isClearable={!isDisabled}
          isSearchable={!isDisabled}
          menuShouldScrollIntoView
          name={name}
          noOptionsMessage={({ inputValue }) =>
            inputValue.length > 2 ? noSuggestionMessage : null
          }
          onBlur={() => {
            // call the validation only if an option was selected
            if (optionWasSelected) {
              setOptionWasSelected(false);

              if (hasError) {
                field.validate();
              }
            }
          }}
          onChange={(newValue, { action }) => {
            // remix remove the focus on the input when clicks with the keyboard to clear the value, so we need to force the focus again
            setOptionWasSelected(action === "select-option");
            if (action === "clear" || action === "select-option") {
              focusOnInput(inputId);
              // TODO: check later why the field.validate() is validating all the fields in the form
              if (hasError) {
                field.validate();
              }
            }
            setCurrentItemValue(newValue);
          }}
          onInputChange={onInputChange}
          options={options}
          placeholder={placeholder ?? ""}
          screenReaderStatus={screenReaderStatus(
            rootLoaderData?.accessibilityTranslations,
          )}
          styles={customStyles()}
          classNames={{
            control: () =>
              classNames("kern-form-input__input bg-white!", {
                "kern-form-input__input--error": hasError,
              }),
            placeholder: () => "absolute",
            singleValue: () => "absolute",
            input: () => "w-full h-full z-50",
          }}
          tabIndex={0}
          value={currentItemValue}
          menuPortalTarget={document.body}
        />
      </div>

      <div key={liveMessageKey} aria-live="polite" className="sr-only">
        {liveMessage}
      </div>

      <InputError id={errorId}>
        {errorMessages?.find((err) => err.code === field.error())?.text ??
          field.error()}
      </InputError>
    </div>
  );
};

export default AutoSuggestInput;
